// pages/CashPayment.tsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { useSupabase } from '../context/SupabaseContext';
import { ArrowLeft, User, Users, CheckCircle, XCircle } from 'lucide-react';

export default function CashPayment() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { state, fetchStudents,addCashPayment, fetchMyLedger} = useSupabase();

  // Get student from URL params
  const { student_id } = useParams<{ studentId: string }>();
  // const studentId  //= searchParams.get('studentId');
  const [studentId, setStudentId] = useState(student_id || undefined);
  const [teacherId, setTeacherId] = useState(undefined);
  const studentName = searchParams.get('studentName') || '';
  const prefillAmount = searchParams.get('amount') || '';

  const [formData, setFormData] = useState({
    amount: parseFloat(prefillAmount) || 0,
    studentId: studentId || undefined,
    studentName,
    note: '',
    date: new Date().toISOString().slice(0, 16),
    teacher_id: teacherId || undefined,
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    console.log("handleSubmit")
    e.preventDefault();
    setLoading(true);

    try {
      addCashPayment(studentId, formData.amount, teacherId)
      fetchMyLedger()
      setSuccess(true);
      setTimeout(() => navigate('/ledger'), 2000);
    } catch (error) {
      console.error('Payment failed:', error);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-md mx-auto p-6">
        <div className="card p-8 text-center">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-emerald-600 mb-2">Payment Recorded!</h2>
          <p className="text-text-light mb-6">
            €{formData.amount.toFixed(2)} cash payment added to ledger
          </p>
          <button
            onClick={() => navigate('/ledger')}
            className="btn-primary w-full"
          >
            Back to Ledger
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <button
            onClick={() => navigate('/ledger')}
            className="p-2 hover:bg-gray-100 rounded-xl"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Record Cash Payment</h1>
            <p className="text-text-light">Add cash payment to student ledger</p>
          </div>
        </div>

        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Student Info */}

            { ['admin', 'teacher'].includes(state.userRole || '') && (
              <div className="card p-4 mb-4">
                <label className="text-xs uppercase tracking-wide text-text-light flex items-center gap-2 mb-2">
                  <User className="w-4 h-4" />
                  Student
                </label>
                <select
                  className="w-full px-3 py-2 border border-border rounded-xl bg-background"
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                >
                  <option value="">Select a student</option>
                  {state.students.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.first_name + ' ' + t.last_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            { ['admin'].includes(state.userRole || '') && (
              <div className="card p-4 mb-4">
                <label className="text-xs uppercase tracking-wide text-text-light flex items-center gap-2 mb-2">
                  <User className="w-4 h-4" />
                  Teacher
                </label>
                <select
                  className="w-full px-3 py-2 border border-border rounded-xl bg-background"
                  value={teacherId}
                  onChange={(e) => setTeacherId(e.target.value)}
                >
                  <option value="">Select a teacher</option>
                  {state.teachers.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.first_name + ' ' + t.last_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Student {student?.first_name } </label>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{formData.studentName}</p>
                  <p className="text-sm text-text-light">ID: {student?.first_name + ' ' + student?.last_name}</p>
                </div>
              </div>
            </div> */}

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount (€)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  amount: parseFloat(e.target.value) || 0
                })}
                className="input-text w-full"
                placeholder="45.50"
                required
              />
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Date
              </label>
              <input
                type="datetime-local"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="input-text w-full"
                required
              />
            </div>

            {/* Note */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note (optional)
              </label>
              <textarea
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                className="input-text w-full h-20 resize-none"
                placeholder="Cash for 2 lessons - March 13"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || formData.amount <= 0}
              className="btn-primary w-full py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Recording...' : `Record €${formData.amount.toFixed(2)} Payment`}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
